// =============================================================================
// app/src/lib/fotoRecortada.ts — a casca de navegador do recorte (lib/recorte.ts).
//
// A rota /imagens/pecas|foto/<id> responde com Access-Control-Allow-Origin
// para o sistema (conferido em 01/10/2026: o cabeçalho é posto no nível do
// site no Caddy, e o `header_down -Access-Control-Allow-Origin` da rota só
// tira o `*` do PostgREST). Então o fetch traz os pixels sem "sujar" o canvas:
// dá para tirar o fundo aqui, no aparelho, sem mexer no servidor nem guardar
// uma segunda cópia da foto no banco (ADR-009).
//
// Três cuidados que o levantamento de 01/10 achou:
//   * a CSP do sistema tem `img-src 'self' data: <api>` — SEM `blob:`. Por
//     isso o resultado é data: URL, não URL.createObjectURL;
//   * foto inexistente responde 200 com corpo VAZIO (e cache de um dia): sem
//     conferir o tamanho, createImageBitmap estoura;
//   * o Safari só solta a memória do canvas com width = 0, e não codifica
//     WebP — o recorte sai em PNG.
//
// Cada foto é recortada UMA vez por sessão (o cache é da URL, que carrega o
// ?v=updated_at — trocar a foto troca a URL). No máximo 2 recortes ao mesmo
// tempo: uma venda de 20 peças não trava o balcão.
//
// A fila (revisão do #254): a RAJADA mais nova vai primeiro — é a lista que
// está na tela; a busca refeita a cada tecla deixava as fotos de agora atrás
// das de três consultas atrás. Dentro da mesma rajada, na ordem em que a lista
// pediu: de cima para baixo (2ª revisão). Pedido que ninguém mais quer (a
// linha saiu da tela) nem baixa a foto. E a rede tem prazo: 8 s, e a tela
// mostra a original.
//
// Qualquer falha devolve null — e a tela mostra a foto como ela é. Recorte é
// enfeite; nunca pode esconder a foto.
// =============================================================================
import { fotoDaCalota } from './fotosDasCalotas';
import { recortar } from './recorte';

/** Miniatura de balcão — não é impressão. */
export const LADO_MINIATURA = 240;
const SIMULTANEOS = 2;
const PRAZO_DA_REDE_MS = 8000;

interface Pedido {
  promessa: Promise<string | null>;
  /** Quantas telas ainda esperam este recorte. Zero na vez dele: não roda. */
  interessados: number;
}

const cache = new Map<string, Pedido>();
let rodando = 0;
/** Os pedidos à espera, cada um com a rajada (o "tique" em que chegou). */
const fila: Array<{ rajada: number; roda: () => void }> = [];
let rajada = 0;
let rajadaAberta = false;

/** Os pedidos do mesmo tique (uma lista montando) são uma rajada só. */
function rajadaAtual(): number {
  if (!rajadaAberta) {
    rajadaAberta = true;
    rajada++;
    setTimeout(() => { rajadaAberta = false; }, 0);
  }
  return rajada;
}

/** A rajada mais nova primeiro; dentro dela, o pedido mais antigo (o de cima). */
function proximaDaFila(): (() => void) | undefined {
  let escolhido = -1;
  for (let i = 0; i < fila.length; i++) {
    if (escolhido < 0 || fila[i]!.rajada > fila[escolhido]!.rajada) escolhido = i;
  }
  return escolhido < 0 ? undefined : fila.splice(escolhido, 1)[0]!.roda;
}

function andarAFila() {
  while (rodando < SIMULTANEOS) {
    const roda = proximaDaFila();
    if (!roda) return;
    roda();
  }
}

function lerComoDataUrl(blob: Blob): Promise<string> {
  return new Promise((ok, erro) => {
    const leitor = new FileReader();
    leitor.onload = () => ok(String(leitor.result));
    leitor.onerror = () => erro(leitor.error);
    leitor.readAsDataURL(blob);
  });
}

async function recortarAgora(url: string, vaos: boolean, lado: number): Promise<string | null> {
  if (typeof document === 'undefined' || typeof createImageBitmap === 'undefined') return null;
  const controle = new AbortController();
  const prazo = setTimeout(() => controle.abort(), PRAZO_DA_REDE_MS);
  let resposta: Response;
  try {
    resposta = await fetch(url, { mode: 'cors', credentials: 'omit', signal: controle.signal });
  } finally {
    clearTimeout(prazo);
  }
  if (!resposta.ok || !(resposta.headers.get('content-type') ?? '').startsWith('image/')) return null;
  const original = await resposta.blob();
  if (original.size === 0) return null;
  const bitmap = await createImageBitmap(original);
  const fator = Math.min(1, lado / Math.max(bitmap.width, bitmap.height));
  const w = Math.max(1, Math.round(bitmap.width * fator));
  const h = Math.max(1, Math.round(bitmap.height * fator));
  const tela = document.createElement('canvas');
  const saida = document.createElement('canvas');
  try {
    tela.width = w; tela.height = h;
    const ctx = tela.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;
    ctx.drawImage(bitmap, 0, 0, w, h);
    const r = recortar(ctx.getImageData(0, 0, w, h), { vaos });
    if (!r) return null;
    saida.width = r.width; saida.height = r.height;
    const sctx = saida.getContext('2d');
    if (!sctx) return null;
    const imagem = sctx.createImageData(r.width, r.height);
    imagem.data.set(r.data);
    sctx.putImageData(imagem, 0, 0);
    const png = await new Promise<Blob | null>((ok) => saida.toBlob(ok, 'image/png'));
    return png ? await lerComoDataUrl(png) : null;
  } finally {
    bitmap.close?.();
    tela.width = 0; saida.width = 0;
  }
}

/**
 * Pede a foto `url` sem o fundo branco (data: URL; null = mostre a original).
 * `soltar()` quando a tela não precisar mais: se o recorte ainda não começou,
 * ele nem baixa a foto.
 */
export function pedirFotoSemFundo(
  url: string, { vaos = false, lado = LADO_MINIATURA }: { vaos?: boolean; lado?: number } = {},
): { promessa: Promise<string | null>; soltar: () => void } {
  const chave = `${vaos ? 'v' : 's'}:${lado}:${url}`;
  let pedido = cache.get(chave);
  if (!pedido) {
    let entregar!: (r: string | null) => void;
    const novo: Pedido = { promessa: new Promise((ok) => { entregar = ok; }), interessados: 0 };
    pedido = novo;
    cache.set(chave, novo);
    fila.push({ rajada: rajadaAtual(), roda: () => {
      if (novo.interessados <= 0) {
        // Ninguém mais quer: sai do cache para um pedido futuro começar do zero.
        cache.delete(chave);
        entregar(null);
        return;
      }
      rodando++;
      recortarAgora(url, vaos, lado).then(
        entregar,
        // Falha de rede (ou o prazo) não fica guardada: a próxima vez tenta de
        // novo. "Não dá para recortar" (null) fica: não adianta repetir.
        () => { cache.delete(chave); entregar(null); },
      ).finally(() => { rodando--; andarAFila(); });
    } });
    setTimeout(andarAFila, 0);
  }
  const alvo = pedido;
  alvo.interessados++;
  let solto = false;
  return {
    promessa: alvo.promessa,
    soltar: () => { if (!solto) { solto = true; alvo.interessados--; } },
  };
}

/** O mesmo, para quem não solta (o mapa veste as vagas e guarda o resultado). */
export function fotoSemFundo(url: string, opcoes: { vaos?: boolean; lado?: number } = {}): Promise<string | null> {
  return pedirFotoSemFundo(url, opcoes).promessa;
}

/**
 * A melhor imagem pronta para a peça: a calota já recortada que mora no app
 * (fotosDasCalotas), se houver — é melhor que qualquer recorte automático,
 * porque tira também os vãos entre os raios.
 */
export const recortePronto = (productId: string | null | undefined): string | undefined =>
  productId ? fotoDaCalota(productId) : undefined;

/**
 * Quando os recortes prontos das calotas foram feitos (commit 120575d, 01/10/2026
 * 09:27 de Rio Grande): foto trocada DEPOIS disso não é a do recorte, e a tela
 * recorta a atual (revisão do #254: a miniatura mostrava a calota antiga).
 */
const RECORTES_PRONTOS_EM = Date.parse('2026-10-01T12:27:42Z');

/**
 * O recorte pronto, só se a foto principal de agora for a mesma de quando ele
 * foi feito: não mudou depois dele E continua na posição 0. Apagar a principal
 * deixa a seguinte com ordem 1 (remover_foto não renumera) — sem esta trava, a
 * miniatura mostrava a calota apagada (2ª revisão do #254). Trocar a principal
 * renumera e atualiza o updated_at, e cai na régua da data.
 */
export function recorteProntoDaFoto(
  productId: string | null | undefined, foto: { updated_at: string; ordem?: number } | null | undefined,
): string | undefined {
  if (!foto || (foto.ordem !== undefined && foto.ordem !== 0)) return undefined;
  const quando = Date.parse(foto.updated_at);
  if (!Number.isFinite(quando) || quando > RECORTES_PRONTOS_EM) return undefined;
  return recortePronto(productId);
}
